<script lang="ts">
	import { LargeProgressBar, LoadingText } from "misc-ui"
	import { getGenerateController } from "./GenerateController.svelte"
	import { getProgressController } from "../ProgressHandler.svelte"
	import { closeOverlay, openConfirmOverlay } from "overlay"
	import { ArrowBack } from "luxedo-icons"
	import { Responsive } from "responsive"
	import GenerationPreview from "./GenerationPreview.svelte"
	import { cancelGeneration } from "../../modules/operations"
	import { getLuxAiController } from "../LuxAIController.svelte"
	import { ErrorIndicator } from "luxedo-comps"

	const genController = getGenerateController()
	let selectedTheme = $derived(genController.selectedTheme)
	let generation = $derived(genController.generation)
	let snapshotSrc = $derived(genController.selectedSnapshot.src)

	const progressController = getProgressController()
	let progress = $derived(progressController.progress[generation?.id])
	let progressText = $derived.by(() => {
		if (!progress || progress == 0) return "Your show is queued and will be starting soon"
		if (progress < 0.9) return "Generating your show"
		return "Putting the finishing touches on your show"
	})
	let errorMessage = $derived(progressController.errors[generation?.id])

	const luxAiController = getLuxAiController()

	function verifyCancel() {
		const overlayId = openConfirmOverlay({
			buttons: {
				confirm: {
					text: "Yes, cancel",
					onClick: async () => {
						await cancelGeneration(generation.id, luxAiController)
						genController.goBack()
					},
				},
				deny: {
					text: "No",
					onClick: () => closeOverlay(overlayId),
				},
			},
			prompt: [`Are you sure you want to cancel generating ${selectedTheme.name}? `],
		})
	}
</script>

<div class="heading" class:isMobile={Responsive.isMobile}>
	<button onclick={() => genController.goBack()} class="icon back-button">
		<ArrowBack />
	</button>
	<div class="heading-text">
		<h1>Generating your {selectedTheme.name} show</h1>
		<p>
			We're now building the full animated theme, perfectly mapped to your projection space. Sit
			tight! This may take a few minutes.
		</p>
	</div>
</div>

{#if errorMessage}
	<ErrorIndicator {errorMessage} linkToSupport />
{:else}
	<GenerationPreview {snapshotSrc} videoSrc={selectedTheme.videoSrc} />
{/if}
<div class="bottom-row progress-preview">
	{#if errorMessage}
		<span class="error-message">{errorMessage}</span>
	{:else}
		<LoadingText content={[progressText]} />
	{/if}
	<div class="flex-row">
		<LargeProgressBar percentage={progress} showPercentage width={"100%"} />
		<button class="small outline-button square-button warn" onclick={verifyCancel}>Cancel</button>
	</div>
</div>

<style>
	.bottom-row {
		flex-direction: column;
		justify-content: flex-end;
		align-items: flex-end;
	}

	.flex-row {
		gap: 1rem;
		width: 100%;
		align-items: center;
	}

	.heading {
		display: flex;
		flex-direction: row;
		align-items: center;
		gap: 1rem;
		margin-bottom: 1rem;
	}

	.progress-preview {
		margin: 2rem 0 1rem 0;
	}

	.back-button {
		height: 3rem;
	}

	.back-button :global(svg) {
		height: 100%;
		width: auto;
	}

	.isMobile .back-button {
		height: 3rem;
	}

	.heading-text {
		flex-grow: 1;
		text-align: center;
	}

	.isMobile .heading-text {
		text-align: start;
	}

	.isMobile .heading-text h1 {
		margin-bottom: 0;
	}
</style>
